import React from 'react';
import { ListGroup, Spinner } from 'react-bootstrap';
import { Select } from '~/components/Select';
import { ServicesContext, useAdminService } from '~/services';
import {
    RoleSelection,
    MarketRestriction,
    useRoleOptions,
    AddedRoleType,
    UserRoleType,
    EntityTypeType,
} from '~/components/Admin';
import { OrgNameTinType, formatOrgName } from '~/util';
import { AddUserRolesType } from '~/services/AdminService/useAddUserRoles';

export interface RoleEntityOptionType {
    label: string;
    value: { id: number; name: string };
}

export interface AddRolesProps {
    show: boolean;
    setAdding: (x: boolean) => void;
    addedRoles: AddedRoleType[];
    setAddedRoles: React.Dispatch<React.SetStateAction<AddedRoleType[]>>;
    userRoles: UserRoleType[];
    entityType: EntityTypeType;
    entityLabel: string;
    userId?: null | number;
}
export function AddRoles({
    show,
    setAdding,
    addedRoles,
    setAddedRoles,
    userRoles,
    entityType,
    entityLabel,
    userId = null,
}: AddRolesProps) {
    const context = React.useContext(ServicesContext);
    const { getRoleEntities, addUserRoles } = useAdminService();
    const { defaultRole } = useRoleOptions();
    const [addLoading, setAddLoading] = React.useState(false);
    const [staticList, setStaticList] = React.useState<RoleEntityOptionType[]>([]);

    React.useEffect(() => {
        (async () => {
            const entityResult = await getRoleEntities(entityType);
            setStaticList(
                entityResult
                    .filter((x) => entityType !== 'organization' || context.user.isAdminInOrg(x.id))
                    .map((x) => ({
                        value: x,
                        label:
                            entityType === 'organization'
                                ? formatOrgName(x as OrgNameTinType)
                                : x.name,
                    }))
            );
        })();
    }, [getRoleEntities, context.user, entityType]);

    // Filter the static entity list to only include items not yet added to this user
    const entityList = React.useMemo(() => {
        return staticList.filter(
            (x) =>
                !userRoles.some((y) => y[entityType as EntityTypeType]?.id === x.value.id) &&
                !addedRoles.some((y) => y.id === x.value.id)
        );
    }, [staticList, userRoles, addedRoles, entityType]);

    function cancelAdding() {
        setAdding(false);
        setAddedRoles([]);
    }

    async function add() {
        if (userId === null) return;
        setAddLoading(true);
        const formattedRoles = addedRoles.map((x) => {
            const data: AddUserRolesType = {
                user: { id: userId },
                [entityType]: { id: x.id },
                [`${entityType}_roles`]: [x.role.value],
            };
            if (x.markets.length > 0) {
                data.markets = x.markets.map((y) => ({ id: y.value.id }));
            }
            return data;
        });
        await addUserRoles(formattedRoles, entityType);
        setAddLoading(false);
        cancelAdding();
    }

    return !show ? null : (
        <>
            <ListGroup.Item>
                <Select
                    label={entityLabel}
                    options={entityList}
                    value={null}
                    onChange={(x) =>
                        setAddedRoles((prev) =>
                            [
                                {
                                    ...x!.value,
                                    role: defaultRole(entityType),
                                    markets: [],
                                } as AddedRoleType,
                            ].concat(prev)
                        )
                    }
                />
            </ListGroup.Item>
            {addedRoles.map((entity) => (
                <ListGroup.Item key={entity.id}>
                    <div className="row align-items-center">
                        <div className="col-9">
                            <label className="form-label">
                                {entityType === 'organization'
                                    ? formatOrgName(entity as OrgNameTinType)
                                    : entity.name}
                            </label>
                            <RoleSelection
                                value={entity.role}
                                entityName={entity.name}
                                entityType={entityType}
                                setRole={(role) =>
                                    setAddedRoles((prev) =>
                                        prev.map((x) => {
                                            if (x.id === entity.id) {
                                                x.role = role;
                                            }
                                            return x;
                                        })
                                    )
                                }
                            />
                            <MarketRestriction
                                entityType={entityType}
                                entityName={entity.name}
                                setValue={(markets) =>
                                    setAddedRoles((prev) =>
                                        prev.map((x) => {
                                            if (x.id === entity.id) {
                                                x.markets = markets;
                                            }
                                            return x;
                                        })
                                    )
                                }
                                value={entity.markets}
                            />
                        </div>
                        <div className="col-3">
                            <button
                                type="button"
                                className=" pull-right btn btn-danger btn-sm"
                                aria-label="Remove"
                                onClick={() =>
                                    setAddedRoles((prev) => prev.filter((x) => x.id !== entity.id))
                                }
                            >
                                <i className="fa-solid fa-close" />
                            </button>
                        </div>
                    </div>
                </ListGroup.Item>
            ))}
            {userId !== null && (
                <ListGroup.Item>
                    <button
                        type="button"
                        className="btn btn-primary pull-right ms-3"
                        onClick={add}
                        disabled={addedRoles.length === 0 || addLoading}
                    >
                        Submit
                        {addLoading && (
                            <Spinner
                                animation="border"
                                variant="light"
                                size="sm"
                                role="status"
                                className="ms-1"
                            />
                        )}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary pull-right"
                        onClick={cancelAdding}
                    >
                        Cancel
                    </button>
                </ListGroup.Item>
            )}
        </>
    );
}
